/**
 * "Seeing a specialist in Canberra" — the patient guide (/guide/seeing-a-specialist).
 *
 * Structured once so the SPA page and the build-time prerender render the
 * same words. General information only, not medical advice. Re-check the
 * Medicare referral-validity rules and phone numbers whenever this changes.
 */
export interface GuideSection {
  id: string;
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export const PATIENT_GUIDE = {
  path: "/guide/seeing-a-specialist",
  title: "Seeing a specialist in Canberra: referrals, Medicare and costs",
  metaTitle: "Seeing a Specialist in Canberra: Referrals & Costs",
  description:
    "How to see a specialist in Canberra: when you need a GP referral, how long referrals last, Medicare rebates, out-of-pocket costs and public clinics.",
  intro:
    "A plain-language guide to seeing a specialist in Canberra. It explains referrals, Medicare and costs so you know what to expect before you book.",
  sections: [
    {
      id: "referral",
      heading: "Do I need a referral?",
      paragraphs: [
        "You need a valid referral to claim a Medicare rebate for a specialist appointment. Referrals usually come from your GP, or from another specialist.",
        "You can usually book a private specialist without a referral, but Medicare will not pay a rebate, so you pay the full fee yourself.",
      ],
    },
    {
      id: "how-long",
      heading: "How long does a referral last?",
      paragraphs: [
        "A referral from a GP is usually valid for 12 months unless your GP writes a different period. A referral from one specialist to another is usually valid for 3 months.",
        "If your referral has expired, ask your GP for a new one before your next appointment.",
      ],
    },
    {
      id: "costs",
      heading: "What will it cost?",
      paragraphs: [
        "Specialists set their own fees. Medicare pays a rebate towards the consultation, and the difference is your out-of-pocket cost. Some specialists bulk bill some patients, so it is worth asking.",
        "Ask the practice about fees when you book. Private health insurance generally does not cover consultations outside hospital.",
      ],
    },
    {
      id: "public",
      heading: "Public clinics at Canberra hospitals",
      paragraphs: [
        "Canberra Health Services runs public specialist outpatient clinics. They need a referral, usually from your GP, and appointments are prioritised by clinical urgency, so waits vary.",
        "Your GP can talk with you about whether a public clinic or a private specialist suits your situation.",
      ],
    },
    {
      id: "choosing",
      heading: "Choosing a specialist",
      paragraphs: [
        "You can ask your GP to refer you to a particular specialist, or to address the referral more generally. Things worth checking with the practice:",
      ],
      bullets: [
        "Whether they see your condition (their areas of interest)",
        "How soon they have appointments",
        "Fees and whether you will have an out-of-pocket cost",
        "Whether telehealth is available for some appointments",
        "Location, parking and accessibility",
      ],
    },
    {
      id: "bring",
      heading: "What to bring to your appointment",
      paragraphs: ["Bring your referral (unless it was sent directly), your Medicare card, a list of your medicines, and any recent scans or test results the specialist may not have."],
    },
    {
      id: "urgent",
      heading: "If it is urgent",
      paragraphs: [
        "In an emergency, call 000 or go to the nearest emergency department. For health advice any time, healthdirect has registered nurses on 1800 022 222.",
      ],
    },
  ] satisfies GuideSection[],
  disclaimer:
    "General information only, not medical advice. Rules can change; check with your GP, the specialist's practice or Services Australia for your situation.",
} as const;

export function patientGuideStructuredData(): Record<string, unknown> {
  const url = `https://canberra.pulseboard.com.au${PATIENT_GUIDE.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: PATIENT_GUIDE.title,
    description: PATIENT_GUIDE.description,
    url,
    audience: { "@type": "PeopleAudience", audienceType: "Patient" },
    about: { "@type": "Thing", name: "Specialist referrals in Canberra" },
    publisher: { "@type": "Organization", name: "PulseBoard" },
    inLanguage: "en-AU",
  };
}
